import { defineStore } from 'pinia';
import { ref } from 'vue';

/* Il menu contestuale, uno solo per tutta l'applicazione.
 *
 * Non è un componente che ogni tessera si porta dietro: sarebbe un pannello
 * nascosto per ogni pin della griglia (millenovecento nodi che non si vedono
 * mai) e soprattutto due menu potrebbero restare aperti insieme. Qui c'è una
 * sola apertura per volta — aprirne un altro chiude il precedente per
 * costruzione, perché lo stato è uno.
 *
 * Chi apre passa le voci già decise: il menu non sa cosa sia una board o un
 * pin, sa solo disegnare un elenco di etichette e chiamare la funzione della
 * voce scelta. È il motivo per cui lo stesso componente serve la colonna, la
 * griglia delle board, le tessere e il lightbox.
 *
 * Forma di una voce:
 *   { etichetta, azione, pericolo?, disabilitata?, spunta? }
 *   { separatore: true }
 * Le voci nulle/false si scartano qui, così chi costruisce l'elenco può
 * scrivere le condizioni in linea senza filtrare a mano.
 */
export const useMenuStore = defineStore('menu', () => {
  const aperto = ref(false);
  const x = ref(0);
  const y = ref(0);
  const titolo = ref('');
  const voci = ref([]);

  /**
   * @param evento  l'evento che ha chiesto il menu (per il punto d'apparizione)
   * @param elenco  le voci, eventualmente con buchi da scartare
   * @param opzioni { titolo } — l'intestazione, di solito il nome dell'oggetto
   */
  function apri(evento, elenco, opzioni = {}) {
    const pulite = potaSeparatori(elenco.filter(Boolean));
    if (!pulite.length) return;
    evento?.preventDefault?.();
    /* Il punto: dove ha cliccato il puntatore. Su un evento da tastiera
     * (il tasto "menu contestuale", o un clic sul tasto ⋯) le coordinate sono
     * a zero: si ripiega sull'angolo dell'elemento che l'ha generato, così il
     * menu non compare in cima a sinistra allo schermo. */
    const punto = puntoDi(evento);
    x.value = punto.x;
    y.value = punto.y;
    titolo.value = opzioni.titolo ?? '';
    voci.value = pulite;
    aperto.value = true;
  }

  function chiudi() {
    aperto.value = false;
    voci.value = [];
    titolo.value = '';
  }

  return { aperto, x, y, titolo, voci, apri, chiudi };
});

function puntoDi(evento) {
  if (evento?.clientX || evento?.clientY) return { x: evento.clientX, y: evento.clientY };
  const rect = evento?.currentTarget?.getBoundingClientRect?.();
  if (rect) return { x: rect.left + rect.width / 2, y: rect.bottom };
  return { x: 0, y: 0 };
}

/* Le condizioni saltano le voci ma non i separatori che le accompagnano: un
 * pin senza fonte lasciava un filetto in cima e due filetti attaccati in
 * mezzo. Qui i separatori di testa, di coda e doppi spariscono. */
function potaSeparatori(elenco) {
  const fuori = [];
  for (const voce of elenco) {
    if (!voce.separatore) {
      fuori.push(voce);
      continue;
    }
    if (fuori.length && !fuori.at(-1).separatore) fuori.push(voce);
  }
  while (fuori.length && fuori.at(-1).separatore) fuori.pop();
  return fuori;
}
